import { z } from 'zod'

import { listSearchSchema, toListParams, withoutListDefaults } from '@/components/common/listSearch'
import type { BlockStockParams } from '@/hooks/queries/block-stock'

/** Codes in a search param: the repeatable exact-match filters. */
const codesParam = z
  .array(z.string())
  .optional()
  .catch(undefined)
  .describe('Exact codes, as a JSON array.')

/**
 * Block stock's view state: the shared ops list search, plus the two filters
 * only this list offers — SKU codes and retailer SKUs, each a list of exact
 * codes (a block matches when any of its lines does).
 *
 * Its own schema rather than two more fields on the shared one, so the other
 * ops lists' `useSearch()` is not widened by filters they never offer.
 */
export const blockStockSearchSchema = listSearchSchema.extend({
  sku: codesParam.describe('SKU codes; a block matches when any of its lines holds one.'),
  retailerSku: codesParam.describe(
    'Retailer SKUs; a block matches when any of its lines carries one.',
  ),
})

export type BlockStockSearch = z.infer<typeof blockStockSearchSchema>

/** Drop defaults and empty code lists, so a URL carries only what differs. */
export function withoutBlockStockDefaults(search: BlockStockSearch): BlockStockSearch {
  const { sku, retailerSku, ...shared } = search
  const pruned: BlockStockSearch = withoutListDefaults(shared)
  if (sku && sku.length > 0) pruned.sku = sku
  if (retailerSku && retailerSku.length > 0) pruned.retailerSku = retailerSku
  return pruned
}

/** What the list endpoint, and its download, are asked for. */
export function toBlockStockParams(search: BlockStockSearch): BlockStockParams {
  return {
    ...toListParams(search),
    skuCodes: search.sku?.length ? search.sku : undefined,
    retailerSkus: search.retailerSku?.length ? search.retailerSku : undefined,
  }
}
